/**
 * The one form for creating a person, used by Employees and by Settings → Users.
 *
 * Two questions, in the office's own words. *Who are they* — an administrator or an
 * employee. *Which doors* — the internal portal, the app, or both. The old form asked
 * for a "role" out of three, one of which ("office") was really a door dressed up as a
 * job title, and nobody could say what picking it would create.
 *
 * Underneath, both doors are one Cognito sign-in and two flags the API checks on every
 * request; see the users route. Tick neither and no sign-in is created at all — the
 * person exists on the books, and can be given a door later from Settings.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Switch } from '@/components/Switch';
import { Field } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, ApiError, type PermissionGrid as Grid, type User } from '@/lib/api';

import { OFFICE_START, PermissionGrid } from './PermissionGrid';

type Kind = 'admin' | 'employee';

export interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  kind: Kind;
  portal: boolean;
  app: boolean;
  /** What an employee with the portal may do there. Ignored for administrators. */
  grid: Grid;
}

const EMPTY: UserFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  kind: 'employee',
  portal: false,
  app: true,
  grid: OFFICE_START,
};

export function useCreateUser(onCreated: (user: User) => void) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<UserFormValues>(EMPTY);

  const create = useMutation({
    mutationFn: () =>
      api.createUser({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        // Optional: without it the person is a record, and the account comes later.
        email: form.email.trim().toLowerCase() || undefined,
        phone: form.phone.trim() || undefined,
        // The API's roles are admin/manager/engineer; the form does not show "manager"
        // because it was only ever "employee with the portal", which is now a tick.
        role: form.kind === 'admin' ? 'admin' : 'engineer',
        portalAccess: form.kind === 'admin' ? true : hasEmail(form) && form.portal,
        appAccess: hasEmail(form) && form.app,
        // Chosen here rather than after: the person's first sign-in already sees what
        // they were meant to see.
        ...(form.kind === 'employee' && form.portal && hasEmail(form) ? { permissions: form.grid } : {}),
        employmentType: 'employee',
      }),
    onSuccess: async ({ user }) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setForm(EMPTY);
      onCreated(user);
    },
  });

  const valid =
    form.firstName.trim().length > 0 &&
    form.lastName.trim().length > 0 &&
    // Blank is fine for an employee — the account is created later. Typed, it has to
    // look like an address. An administrator exists to sign in, so needs one now.
    (hasEmail(form) || (form.email.trim() === '' && form.kind === 'employee')) &&
    !create.isPending;

  return { form, setForm, create, valid };
}

function hasEmail(form: UserFormValues): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
}

/** The fields only. The caller supplies the modal, the buttons and the submit. */
export function UserFormFields({
  form,
  setForm,
  onSubmit,
}: {
  form: UserFormValues;
  setForm: (v: UserFormValues) => void;
  onSubmit?: () => void;
}) {
  const { t } = useI18n();

  const noEmail = form.kind === 'employee' && form.email.trim() === '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit?.();
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t('access.firstName')}>
          <input
            className="input"
            required
            autoFocus
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
        </Field>
        <Field label={t('access.lastName')}>
          <input
            className="input"
            required
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
        </Field>
        <Field
          label={form.kind === 'admin' ? t('access.email') : `${t('access.email')} (${t('common.optional')})`}
        >
          <input
            className="input"
            type="email"
            required={form.kind === 'admin'}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label={t('access.phone')}>
          <input
            className="input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Field>
      </div>

      {/* Who they are: two choices, side by side, not a dropdown of three. */}
      <div>
        <div className="mb-1.5 text-xs font-semibold text-muted">{t('access.kind')}</div>
        <div className="grid grid-cols-2 gap-2">
          {(['employee', 'admin'] as const).map((kind) => {
            const active = form.kind === kind;
            return (
              <button
                key={kind}
                type="button"
                aria-pressed={active}
                onClick={() =>
                  setForm({ ...form, kind, portal: kind === 'admin' ? true : form.portal })
                }
                className={[
                  'rounded-lg border p-3 text-start transition-colors',
                  active
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                    : 'border-border bg-surface hover:bg-surface-alt',
                ].join(' ')}
              >
                <div className={`text-sm font-semibold ${active ? 'text-primary' : ''}`}>
                  {t(kind === 'admin' ? 'access.kindAdmin' : 'access.kindEmployee')}
                </div>
                <div className="text-xs text-muted mt-0.5 leading-snug">
                  {t(kind === 'admin' ? 'access.kindAdminHint' : 'access.kindEmployeeHint')}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Which doors. An administrator always has the portal, so that box is fixed. */}
      <div>
        <div className="mb-1.5 text-xs font-semibold text-muted">{t('access.doors')}</div>
        <div className="divide-y divide-border rounded-lg border border-border">
          {[
            {
              key: 'portal' as const,
              title: t('access.doorPortal'),
              hint: t('access.doorPortalHint'),
              locked: form.kind === 'admin',
            },
            { key: 'app' as const, title: t('access.doorApp'), hint: t('access.doorAppHint'), locked: false },
          ].map((door) => (
            <div key={door.key} className="flex items-center justify-between gap-4 px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-sm font-semibold">{door.title}</div>
                <div className="mt-0.5 text-xs leading-snug text-muted">{door.hint}</div>
              </div>
              <Switch
                // No address, no account: shown off rather than silently ignored.
                checked={noEmail ? false : form[door.key]}
                label={door.title}
                disabled={door.locked || noEmail}
                onChange={(on) => setForm({ ...form, [door.key]: on })}
              />
            </div>
          ))}
        </div>
        {noEmail ? (
          <p className="mt-2 rounded-lg bg-surface-alt px-3 py-2.5 text-xs text-muted">
            {t('access.noEmailHint')}
          </p>
        ) : !form.portal && !form.app ? (
          <p className="mt-2 text-xs text-muted">{t('access.noDoorsHint')}</p>
        ) : null}
      </div>

      {form.kind === 'employee' && form.portal && !noEmail ? (
        <div>
          <div className="mb-1.5 text-xs font-semibold text-muted">{t('access.permissionsForNew')}</div>
          <PermissionGrid value={form.grid} onChange={(grid) => setForm({ ...form, grid })} />
        </div>
      ) : form.kind === 'admin' ? (
        <p className="rounded-lg bg-surface-alt px-3 py-2.5 text-xs text-muted">{t('access.fullAccess')}</p>
      ) : null}
    </form>
  );
}

export function createErrorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}
